import React, { useEffect } from 'react'
import PropTypes from 'prop-types'

import {
  useFlexLayout,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'

import {
  TableStyle,
  TableWrap,
  TBody,
  TBodyRow,
  TData,
  TDLoading,
  THead,
  THeader,
  THeadRow,
  CaptionStyle,
} from './styles'

import addSelectionColumn from './services/addSelectionColumn'
import getHeaderProps from './services/getHeaderProps'
import getCellProps from './services/getCellProps'

import Pagination from './components/Pagination'
import SortIcon from './components/SortIcon'

function Table({
  id,
  data,
  columns,
  hasHeader,
  hasSelect,
  hasCompleteSelect,
  getIsSelectedAll,
  getData,
  hasPagination,
  searchUrl = false,
  extraParams = {},
  hasSortBy,
  onSelect,
  onSelectAll,
  onSelectPage,
  onSelectRow,
  onSelectIdList,
  selectAll,
  fetchData,
  loading,
  onClickRow,
  ariaLabel,
  ariaDescribedBy,
  caption,
  pageCount: controlledPageCount,
  pageSize: initialPageSize,
  pageIndex: initialPageIndex,
  sortBy: initialSortBy,
  ...props
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    selectedFlatRows,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: initialPageIndex || 0,
        sortBy: hasSortBy && initialSortBy,
      },
      manualPagination: true,
      manualSortBy: true,
      manualRowSelectedKey: 'isSelected',
      pageCount: controlledPageCount,
      autoResetSortBy: false,
      autoResetPage: false,
    },
    useFlexLayout,
    hasSortBy && useSortBy,
    hasPagination && usePagination,
    hasSelect && useRowSelect,
    (hooks) => {
      hasSelect &&
        addSelectionColumn({
          hooks,
          selectAll,
          hasPagination,
          hasCompleteSelect,
          onSelectAll,
          onSelectPage,
          onSelectRow,
          onSelectIdList,
          getIsSelectedAll,
          getData,
        })
    }
  )

  useEffect(() => {
    hasSelect &&
      onSelect &&
      onSelect(selectedFlatRows.map((row) => row.original))
  }, [selectedFlatRows, hasSelect, onSelect])

  useEffect(() => {
    if (fetchData) fetchData({ pageIndex, pageSize, sortBy })
  }, [fetchData, pageIndex, pageSize, sortBy])

  return (
    <>
      <TableWrap {...props}>
        <TableStyle
          {...getTableProps()}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          id={id}
        >
          <CaptionStyle>{caption}</CaptionStyle>
          <THead hidden={!hasHeader}>
            {headerGroups.map((headerGroup) => (
              <THeadRow {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <THeader {...getHeaderProps(column, hasSortBy, id)}>
                    {column.render('Header')}
                    <SortIcon hasSortBy={hasSortBy} column={column} />
                  </THeader>
                ))}
              </THeadRow>
            ))}
          </THead>
          <TBody {...getTableBodyProps()}>
            {(hasPagination ? page : rows).map((row) => {
              prepareRow(row)
              return (
                <TBodyRow
                  {...row.getRowProps()}
                  onClick={onClickRow ? () => onClickRow(row) : null}
                  hasClick={!!onClickRow}
                >
                  {row.cells.map((cell) => {
                    return (
                      <TData {...getCellProps(cell, id)}>
                        {cell.render('Cell')}
                      </TData>
                    )
                  })}
                </TBodyRow>
              )
            })}
            {loading && (
              <TBodyRow>
                <TDLoading colSpan="10000">Carregando...</TDLoading>
              </TBodyRow>
            )}
          </TBody>
        </TableStyle>
      </TableWrap>
      {hasPagination && (
        <Pagination
          {...{
            gotoPage,
            previousPage,
            canPreviousPage,
            nextPage,
            pageCount,
            canNextPage,
            pageOptions,
            pageIndex,
            pageSize,
            searchUrl,
            extraParams,
          }}
        />
      )}
    </>
  )
}

Table.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  columns: PropTypes.arrayOf(PropTypes.object),
  data: PropTypes.arrayOf(PropTypes.object),
  hasHeader: PropTypes.bool,
  hasSelect: PropTypes.bool,
  selectAll: PropTypes.bool,
  onSelect: PropTypes.func,
  hasPagination: PropTypes.bool,
  searchUrl: PropTypes.bool,
  hasSortBy: PropTypes.bool,
  pageCount: PropTypes.number,
  pageIndex: PropTypes.number,
  pageSize: PropTypes.number,
  sortBy: PropTypes.array,
  ariaLabel: PropTypes.string,
  ariaDescribedBy: PropTypes.string,
  caption: PropTypes.string,
}

Table.defaultProps = {
  selectAll: true,
  hasHeader: true,
  hasSelect: false,
  hasPagination: false,
  searchUrl: false,
  hasSortBy: false,
  pageCount: 0,
  pageSize: 10,
  sortBy: [],
}

export default Table
